import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Star, Users, Clock, Filter, ShoppingCart, Heart, Bell, Layers, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { getLocalizedField, interpolate } from '../utils/localizedContent';


// Componente popup de cartas de sobre
function BoosterCardsPopup({ booster, onClose, lang, t }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCards = async () => {
      try {
        const { data } = await axios.get(`${API}/tcg/sets/${booster.set_id}/cards`, {
          params: { tcg_type: booster.tcg_type, page: 1, page_size: 12 }
        });
        setCards(data.cards || []);
      } catch (err) {
        console.error('Failed to fetch cards:', err);
      }
      setLoading(false);
    };
    fetchCards();
  }, [booster]);

  return (
    <div 
      className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 bg-[#12151A] border border-[#C9A962]/30 rounded-xl shadow-2xl overflow-hidden"
      onMouseEnter={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="bg-[#0D5E4A]/20 px-3 py-2 border-b border-[#C9A962]/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers size={14} className="text-[#C9A962]" />
            <span className="text-[#F5F0E6] font-semibold text-xs truncate">{getLocalizedField(booster, 'name', lang, booster.name)}</span>
          </div>
          <button onClick={onClose} className="text-[#A8B5AE] hover:text-white">
            <X size={14} />
          </button>
        </div>
        <p className="text-[#A8B5AE] text-[10px]">{booster.set_code} · {booster.tcg_type}</p>
      </div>
      
      {/* Cards Preview */}
      <div className="p-2">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 size={20} className="animate-spin text-[#C9A962]" />
          </div>
        ) : cards.length === 0 ? (
          <p className="text-[#A8B5AE] text-xs text-center py-4">
            {t('store.loading_cards_error')}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-1.5">
              {cards.slice(0, 8).map((card, idx) => (
                <div key={card.external_id || idx} className="aspect-[2.5/3.5] rounded overflow-hidden bg-[#0F1F1A]">
                  {card.image_url ? (
                    <img src={card.image_url} alt={card.name} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#A8B5AE] text-[8px] p-0.5 text-center">
                      {card.name?.slice(0, 15)}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <Link 
              to={`/boosters/${booster.booster_id}`}
              className="block text-center text-[#C9A962] text-xs mt-2 hover:underline"
            >
              {interpolate(t('store.view_all_cards'), { count: booster.total_cards || '' })}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export default function Store() {
  const [products, setProducts] = useState([]);
  const [boosters, setBoosters] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [category, setCategory] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minRating, setMinRating] = useState('');
  const [rentableOnly, setRentableOnly] = useState(false);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [wishlist, setWishlist] = useState(new Set());
  const [wishlistLoading, setWishlistLoading] = useState({});
  const [activeBoosterPopup, setActiveBoosterPopup] = useState(null);
  const { addItem } = useCart();
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSearchDebouncing = search !== debouncedSearch;

  useEffect(() => {
    const cat = searchParams.get('category') || '';
    const searchTerm = searchParams.get('search') || '';
    const sort = searchParams.get('sort') || '';
    const minPriceParam = searchParams.get('min_price') || '';
    const maxPriceParam = searchParams.get('max_price') || '';
    const minRatingParam = searchParams.get('min_rating') || '';
    const rentableOnlyParam = searchParams.get('rentable_only');
    const inStockOnlyParam = searchParams.get('in_stock_only');
    const pageParam = Number(searchParams.get('page') || '1');

    setCategory(cat);
    setSearch(searchTerm);
    setSortBy(sort);
    setMinPrice(minPriceParam);
    setMaxPrice(maxPriceParam);
    setMinRating(minRatingParam);
    setRentableOnly(rentableOnlyParam === '1' || rentableOnlyParam === 'true');
    setInStockOnly(inStockOnlyParam === '1' || inStockOnlyParam === 'true');
    setPage(Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1);
  }, [searchParams]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    const nextParams = new URLSearchParams();
    if (category) nextParams.set('category', category);
    if (search) nextParams.set('search', search);
    if (sortBy) nextParams.set('sort', sortBy);
    if (minPrice !== '') nextParams.set('min_price', minPrice);
    if (maxPrice !== '') nextParams.set('max_price', maxPrice);
    if (minRating !== '') nextParams.set('min_rating', minRating);
    if (rentableOnly) nextParams.set('rentable_only', '1');
    if (inStockOnly) nextParams.set('in_stock_only', '1');
    if (page > 1) nextParams.set('page', String(page));

    const nextQuery = nextParams.toString();
    if (nextQuery !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    category,
    search,
    sortBy,
    minPrice,
    maxPrice,
    minRating,
    rentableOnly,
    inStockOnly,
    page,
    searchParams,
    setSearchParams
  ]);

  // Cargar lista de deseos del usuario
  useEffect(() => {
    if (user) {
      axios.get(`${API}/wishlist`).then(r => {
        const ids = new Set((r.data.items || []).map(i => i.product_id));
        setWishlist(ids);
      }).catch((err) => console.error('Failed to fetch wishlist:', err));
    }
  }, [user]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 12 };
      if (category) params.category = category;
      if (debouncedSearch) params.search = debouncedSearch;
      if (minPrice !== '') params.min_price = Number(minPrice);
      if (maxPrice !== '') params.max_price = Number(maxPrice);
      if (minRating !== '') params.min_rating = Number(minRating);
      if (rentableOnly) params.rentable_only = true;
      if (inStockOnly) params.in_stock_only = true;
      const [productsRes, boostersRes] = await Promise.all([
        axios.get(`${API}/products`, { params }),
        axios.get(`${API}/boosters`).catch(() => ({ data: { boosters: [] } }))
      ]);
      
      let prods = productsRes.data.products || [];
      if (sortBy === 'price_asc') prods = [...prods].sort((a, b) => a.price - b.price);
      else if (sortBy === 'price_desc') prods = [...prods].sort((a, b) => b.price - a.price);
      else if (sortBy === 'rating') prods = [...prods].sort((a, b) => (b.rating || 0) - (a.rating || 0));
      setProducts(prods);
      setTotal(productsRes.data.total || 0);
      setPages(productsRes.data.pages || 1);
      
      // Crear mapa de búsqueda de sobres
      const boosterMap = {};
      (boostersRes.data.boosters || []).forEach(b => {
        boosterMap[b.booster_id] = b;
      });
      setBoosters(boosterMap);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
    setLoading(false);
  }, [category, debouncedSearch, sortBy, page, minPrice, maxPrice, minRating, rentableOnly, inStockOnly]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const toggleWishlist = async (productId) => {
    if (!user) return;
    setWishlistLoading(prev => ({ ...prev, [productId]: true }));
    try {
      if (wishlist.has(productId)) {
        await axios.delete(`${API}/wishlist/${productId}`);
        setWishlist(prev => { const n = new Set(prev); n.delete(productId); return n; });
      } else {
        await axios.post(`${API}/wishlist/add`, { product_id: productId });
        setWishlist(prev => new Set([...prev, productId]));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setWishlistLoading(prev => ({ ...prev, [productId]: false }));
    }
  };

  const handleReserve = async (productId) => {
    if (!user) return;
    try {
      await axios.post(`${API}/product-reservations`, { product_id: productId, quantity: 1 });
      alert(t('product_reserve.success'));
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    }
  };

  const clearAdvancedFilters = () => {
    setMinPrice('');
    setMaxPrice('');
    setMinRating('');
    setRentableOnly(false);
    setInStockOnly(false);
    setPage(1);
  };

  const buildDefaultCategoryFilters = useCallback(() => ([
    { key: '', label: t('store.all') },
    { key: 'board_game', label: t('store.board_games') },
    { key: 'tcg', label: 'TCG' },
    { key: 'accessory', label: t('store.accessories') },
  ]), [t]);

  const [categoryFilters, setCategoryFilters] = useState(buildDefaultCategoryFilters);

  // Cargar filtros de categoría dinámicos desde tipos de producto
  useEffect(() => {
    axios.get(`${API}/product-types`)
      .then(r => {
        const types = r.data.types || [];
        if (types.length > 0) {
          setCategoryFilters([
            { key: '', label: t('store.all') },
            ...types.map(pt => ({
              key: pt.type_id,
              label: getLocalizedField(pt, 'name', lang, pt.name)
            }))
          ]);
        } else {
          setCategoryFilters(buildDefaultCategoryFilters());
        }
      })
      .catch(() => {
        setCategoryFilters(buildDefaultCategoryFilters());
      });
  }, [buildDefaultCategoryFilters, lang, t]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 page-enter">
      <h1 className="text-4xl font-black text-foreground mb-8" style={{ fontFamily: 'Cinzel, serif' }}>{t('store.title')}</h1>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder={t('store.search_placeholder')}
            className="w-full bg-card border border-border rounded-xl pl-10 pr-4 py-3 text-foreground text-sm outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/50"
            data-testid="store-search" />
          {isSearchDebouncing && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Loader2 size={12} className="animate-spin" />
              {t('store.searching')}
            </span>
          )}
        </div>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          className="bg-card border border-border rounded-xl px-4 py-3 text-sm text-muted-foreground outline-none focus:border-primary transition-colors"
          data-testid="store-sort">
          <option value="">{t('store.sort')}</option>
          <option value="price_asc">{t('store.sort_price_asc')}</option>
          <option value="price_desc">{t('store.sort_price_desc')}</option>
          <option value="rating">{t('store.sort_rating')}</option>
        </select>
      </div>

      {/* Advanced filters */}
      <div className="mb-8 p-4 rounded-xl border border-border bg-card">
        <div className="flex flex-col lg:flex-row gap-3">
          <input
            type="number"
            min="0"
            step="0.01"
            value={minPrice}
            onChange={e => { setMinPrice(e.target.value); setPage(1); }}
            placeholder={t('store.min_price')}
            className="bg-background border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors"
            data-testid="store-min-price"
          />
          <input
            type="number"
            min="0"
            step="0.01"
            value={maxPrice}
            onChange={e => { setMaxPrice(e.target.value); setPage(1); }}
            placeholder={t('store.max_price')}
            className="bg-background border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors"
            data-testid="store-max-price"
          />
          <select
            value={minRating}
            onChange={e => { setMinRating(e.target.value); setPage(1); }}
            className="bg-background border border-border rounded-xl px-3 py-2.5 text-sm text-muted-foreground outline-none focus:border-primary transition-colors"
            data-testid="store-min-rating"
          >
            <option value="">{t('store.min_rating')}</option>
            <option value="5">5+</option>
            <option value="4">4+</option>
            <option value="3">3+</option>
            <option value="2">2+</option>
            <option value="1">1+</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-muted-foreground px-1">
            <input
              type="checkbox"
              checked={rentableOnly}
              onChange={e => { setRentableOnly(e.target.checked); setPage(1); }}
              data-testid="store-rentable-only"
            />
            {t('store.rentable_only')}
          </label>
          <label className="flex items-center gap-2 text-sm text-muted-foreground px-1">
            <input
              type="checkbox"
              checked={inStockOnly}
              onChange={e => { setInStockOnly(e.target.checked); setPage(1); }}
              data-testid="store-in-stock-only"
            />
            {t('store.in_stock_only')}
          </label>
          <button
            type="button"
            onClick={clearAdvancedFilters}
            className="px-4 py-2 rounded-xl border border-primary/30 text-primary hover:bg-primary/10 transition-colors text-sm font-semibold"
            data-testid="store-clear-advanced-filters"
          >
            {t('store.clear')}
          </button>
        </div>
      </div>

      {/* Category tabs — loaded dynamically from product types */}
      <div className="flex gap-2 mb-8 flex-wrap">
        {categoryFilters.map(tab => (
          <button key={tab.key} onClick={() => { setCategory(tab.key); setPage(1); }}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${category === tab.key ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:text-foreground hover:border-primary/40'}`}
            data-testid={`category-${tab.key || 'all'}`}>{tab.label}</button>
        ))}
      </div>

      {/* Stats */}
      <p className="text-muted-foreground text-sm mb-6">{interpolate(t('store.products_count'), { count: total })}</p>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => <div key={`skeleton-${i}`} className="card-glass overflow-hidden"><div className="aspect-square skeleton"></div><div className="p-4 space-y-2"><div className="h-4 skeleton rounded"></div><div className="h-4 w-2/3 skeleton rounded"></div></div></div>)}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">
          <Filter size={48} className="mx-auto mb-4 opacity-30" />
          <p>{t('store.no_products')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
          {products.map(product => {
            const linkedBooster = product.booster_id ? boosters[product.booster_id] : null;
            return (
            <div key={product.product_id} className="card-glass overflow-hidden group relative" data-testid={`product-${product.product_id}`}>
              <Link to={`/store/${product.product_id}`}>
                <div className="aspect-square overflow-hidden relative">
                  <img src={product.images?.[0] || 'https://via.placeholder.com/300'} alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute top-2 left-2 flex gap-1 flex-wrap">
                    {product.category === 'tcg' && <span className="bg-[#0D5E4A]/90 text-[#F5F0E6] text-xs px-2 py-0.5 rounded-full font-semibold">TCG</span>}
                    {product.is_rentable && <span className="bg-[#C9A962]/90 text-[#0A1612] text-xs px-2 py-0.5 rounded-full font-semibold">{t('store.rent_badge')}</span>}
                  </div>
                </div>
              </Link>
              <div className="p-4">
                <Link to={`/store/${product.product_id}`}>
                  <h3 className="text-[#F5F0E6] font-semibold text-sm leading-snug mb-1 hover:text-[#C9A962] transition-colors" style={{ fontFamily: 'Cinzel, serif' }}>
                    {getLocalizedField(product, 'name', lang, product.name)}
                  </h3>
                </Link>
                
                {/* Booster Badge with Hover Popup */}
                {linkedBooster && (
                  <div 
                    className="relative mb-2"
                    onMouseEnter={() => setActiveBoosterPopup(product.product_id)}
                    onMouseLeave={() => setActiveBoosterPopup(null)}
                  >
                    <div className="flex items-center gap-1 cursor-pointer group/booster">
                      <Layers size={12} className="text-[#C9A962]" />
                      <span className="text-[#C9A962] text-xs hover:underline truncate max-w-[120px]">
                        {getLocalizedField(linkedBooster, 'name', lang, linkedBooster.name)}
                      </span>
                    </div>
                    {activeBoosterPopup === product.product_id && (
                      <BoosterCardsPopup 
                        booster={linkedBooster} 
                        onClose={() => setActiveBoosterPopup(null)}
                        lang={lang}
                        t={t}
                      />
                    )}
                  </div>
                )}
                
                <div className="flex items-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => <Star key={`star-${product.product_id}-${i}`} size={11} className={i < Math.round(product.rating) ? 'text-[#C9A962] fill-[#C9A962]' : 'text-[#A8B5AE]'} />)}
                  <span className="text-[#A8B5AE] text-xs ml-1">({product.review_count || 0})</span>
                </div>
                {product.min_players && (
                  <div className="flex items-center gap-3 text-xs text-[#A8B5AE] mb-2">
                    <span className="flex items-center gap-1"><Users size={11} />{product.min_players}-{product.max_players}</span>
                    {product.duration_minutes && <span className="flex items-center gap-1"><Clock size={11} />{product.duration_minutes}{t('store.minutes')}</span>}
                  </div>
                )}
                <div className="flex items-center justify-between mt-3">
                  <span className="text-[#C9A962] font-bold text-base">{product.price?.toFixed(2)}€</span>
                  <div className="flex gap-1.5">
                    {/* Wishlist button */}
                    {user && (
                      <button 
                        onClick={() => toggleWishlist(product.product_id)}
                        disabled={wishlistLoading[product.product_id]}
                        className={`p-2 rounded-lg transition-colors ${wishlist.has(product.product_id) ? 'bg-[#C45B4A]/20 text-[#F87171]' : 'bg-[#0F1F1A] text-[#A8B5AE] hover:text-[#F87171]'}`}
                        data-testid={`wishlist-btn-${product.product_id}`}
                        title={wishlist.has(product.product_id) ? t('wishlist.remove') : t('wishlist.add')}
                      >
                        <Heart size={14} className={wishlist.has(product.product_id) ? 'fill-current' : ''} />
                      </button>
                    )}
                    {product.stock > 0 ? (
                      <button onClick={() => addItem(product)}
                        className="p-2 rounded-lg bg-[#C9A962]/10 text-[#C9A962] hover:bg-[#C9A962]/20 transition-colors"
                        data-testid={`cart-btn-${product.product_id}`}>
                        <ShoppingCart size={14} />
                      </button>
                    ) : user ? (
                      <button 
                        onClick={() => handleReserve(product.product_id)}
                        className="p-2 rounded-lg bg-[#2D8B70]/20 text-[#2D8B70] hover:bg-[#2D8B70]/30 transition-colors"
                        data-testid={`reserve-btn-${product.product_id}`}
                        title={t('product_reserve.reserve')}
                      >
                        <Bell size={14} />
                      </button>
                    ) : (
                      <span className="text-xs text-[#C45B4A] px-2 py-1 bg-[#C45B4A]/10 rounded">{t('store.out_of_stock')}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex justify-center gap-2 mt-10">
          {[...Array(pages)].map((_, i) => (
            <button key={`page-btn-${i + 1}`} onClick={() => setPage(i + 1)}
              className={`w-9 h-9 rounded-lg text-sm font-semibold transition-all ${page === i + 1 ? 'bg-[#C9A962] text-[#0A1612]' : 'bg-[#0F1F1A] border border-[#C9A962]/20 text-[#A8B5AE] hover:text-[#F5F0E6]'}`}
              data-testid={`page-${i + 1}`}>{i + 1}</button>
          ))}
        </div>
      )}
    </div>
  );
}
